/**
 * Order History Page - Tailwind Version
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/de";
import moment from "moment";
import {
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  History as HistoryIcon,
} from "lucide-react";
import { Order, OrderStatus } from "@tasty/api";
import { OrderClient } from "../../api-client";
import { useRestaurantContext } from "../../context/restaurant-context";
import { usePersistentStorageValue } from "../../utils/usePersistentStorageValue";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { cn, formatOrderId, formatPrice } from "../../lib/utils";
import { toast } from "sonner";
import { useDebounce } from "../../hooks/useDebounce";
import { Sheet } from "@/components/ui/sheet";
import { OrderDetailsDrawer } from "../orders/OrderDetailsDrawer";

dayjs.locale("de");

const STATUS_OPTIONS: Array<{ value: "ALL" | OrderStatus; label: string }> = [
  { value: "ALL", label: "Alle Status" },
  { value: OrderStatus.COMPLETED, label: "Abgeschlossen" },
  { value: OrderStatus.DECLINED_BY_RESTAURANT, label: "Abgelehnt" },
];

export default function OrderHistoryPage() {
  const { restaurant } = useRestaurantContext();

  const [loading, setLoading] = useState(false);
  const [orderHistory, setOrderHistory] = usePersistentStorageValue<Order[]>(
    "history-orders",
    []
  );

  // Filter/Range
  const today = dayjs().format("YYYY-MM-DD");
  const [fromDate, setFromDate] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD")
  );
  const debouncedFromDate = useDebounce(fromDate, 600); // Senior Fix: Debounce date input

  const [statusFilter, setStatusFilter] = useState<"ALL" | OrderStatus>("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);

  // Drawer
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!restaurant?.id) return;
    const startIso = moment(debouncedFromDate)
      .utc()
      .startOf("day")
      .toISOString();

    setLoading(true);
    try {
      const data = await OrderClient.queryListPaginated({
        filter: {
          and: [
            { createdAt: { gt: startIso } },
            { orderRestaurantId: { eq: restaurant.id } },
            {
              or: [
                { status: { eq: OrderStatus.COMPLETED } },
                { status: { eq: OrderStatus.DECLINED_BY_RESTAURANT } },
              ],
            },
          ],
        },
      });

      const sorted = (data || []).sort(
        (a, b) =>
          new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
      );
      setOrderHistory(sorted);
      setPage(0);
    } catch (e) {
      toast.error("Historie konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [restaurant?.id, debouncedFromDate, setOrderHistory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredOrders = useMemo(() => {
    let list = orderHistory;
    if (statusFilter !== "ALL")
      list = list.filter((o) => o.status === statusFilter);
    if (debouncedSearch.trim()) {
      const term = debouncedSearch.trim().toLowerCase();
      list = list.filter((o) =>
        formatOrderId(o.id).toLowerCase().includes(term)
      );
    }
    return list;
  }, [orderHistory, statusFilter, debouncedSearch]);

  const pageItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredOrders.slice(start, start + rowsPerPage);
  }, [filteredOrders, page, rowsPerPage]);

  return (
    <div className="w-full space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b pb-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-black tracking-tighter">History</h1>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-60">
            {filteredOrders.length} Completed orders since{" "}
            {dayjs(debouncedFromDate).format("MMM DD, YYYY")}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="h-9 px-4 rounded-lg font-bold border-muted-foreground/20"
        >
          <RefreshCw
            className={cn("h-4 w-4 mr-2", loading && "animate-spin")}
          />{" "}
          Aktualisieren
        </Button>
      </div>

      {/* Modern Filter Bar */}
      <div className="flex flex-col lg:flex-row gap-4 bg-muted/10 p-4 rounded-2xl border border-dashed border-muted-foreground/20">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">
              From Date
            </label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={today}
              className="h-10 bg-background border-muted-foreground/10 rounded-xl font-bold text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">
              Status
            </label>
            <select
              className="w-full h-10 rounded-xl border border-muted-foreground/10 bg-background px-3 text-xs font-bold uppercase tracking-wide outline-none focus:ring-2 focus:ring-ring transition-all"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">
              Search ID
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                className="pl-9 h-10 bg-background border-muted-foreground/10 rounded-xl text-xs font-bold"
                placeholder="#73A5"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1 border rounded-2xl overflow-hidden bg-muted-foreground/10 border-muted-foreground/10 shadow-sm">
        {pageItems.length === 0 ? (
          <div className="bg-background p-20 text-center">
            <HistoryIcon className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground font-bold">
              No orders found for this period.
            </p>
          </div>
        ) : (
          pageItems.map((order) => (
            <div
              key={order.id}
              className="group flex items-center justify-between p-4 bg-background hover:bg-muted/5 transition-all cursor-pointer border-b last:border-b-0 border-muted-foreground/5"
              onClick={() => {
                setSelectedOrderId(order.id);
                setIsDrawerOpen(true);
              }}
            >
              <div className="flex items-center gap-6 flex-1 min-w-0">
                <div className="min-w-0">
                  <span className="font-black text-sm tabular-nums block leading-tight">
                    #{order.id.slice(-4).toUpperCase()}
                  </span>
                  <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mt-1">
                    {dayjs(order.createdAt).format("MMM DD, YYYY • HH:mm")}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[9px] font-black uppercase tracking-tighter px-2 h-5 border-muted-foreground/20",
                      order.status === OrderStatus.COMPLETED
                        ? "text-success border-success/30"
                        : "text-destructive border-destructive/30"
                    )}
                  >
                    {order.status}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-8">
                <div className="text-right">
                  <span className="text-sm font-black tabular-nums block leading-tight">
                    {formatPrice(order.total || 0)}
                  </span>
                  <span className="text-[9px] text-muted-foreground font-bold uppercase">
                    Online Payment
                  </span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredOrders.length > rowsPerPage && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="h-9 px-4 rounded-lg font-bold"
          >
            Back
          </Button>
          <span className="text-xs font-bold text-muted-foreground px-4">
            Page {page + 1} of {Math.ceil(filteredOrders.length / rowsPerPage)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * rowsPerPage >= filteredOrders.length}
            className="h-9 px-4 rounded-lg font-bold"
          >
            Next
          </Button>
        </div>
      )}

      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        {selectedOrderId && (
          <OrderDetailsDrawer
            orderId={selectedOrderId}
            onClose={() => setIsDrawerOpen(false)}
            onStatusChanged={() => fetchData()}
          />
        )}
      </Sheet>
    </div>
  );
}
